<script setup lang="ts">
import Footer from '@/components/Footer.vue';
import Header from '@/components/Header.vue';
import Accordion from '@/components/ui/Accordion.vue';
import AccordionContent from '@/components/ui/AccordionContent.vue';
import AccordionItem from '@/components/ui/AccordionItem.vue';
import AccordionTrigger from '@/components/ui/AccordionTrigger.vue';
import { Head } from '@inertiajs/vue3';
import { Coins, HelpCircle, Lock, Mail, MessageCircle, Shield } from 'lucide-vue-next';
interface FAQ {
    question: string;
    answer: string;
}

interface FAQCategory {
    icon: any;
    title: string;
    faqs: FAQ[];
}

const faqCategories: FAQCategory[] = [
    {
        icon: Shield,
        title: 'Digital Safe Platform',
        faqs: [
            {
                question: 'What is Digital Safe?',
                answer: 'Digital Safe is an innovative platform for storing and verifying valuable documents and physical assets in digital form. It combines advanced encryption, certification, and decentralized storage technologies to give users full control over their digital assets.',
            },
            {
                question: 'How does Digital Safe work?',
                answer: 'Users can register banknotes and other valuable items by scanning them using a mobile phone camera or special reader. These items are then converted into digital form and stored in a secure database with unique digital IDs.',
            },
            {
                question: 'What security measures are in place?',
                answer: 'Digital Safe uses advanced security mechanisms including two-factor authorization, data encryption, biometric user data, and blockchain technology with the efficient and secure X1 network to ensure transparency, authenticity, and security.',
            },
            {
                question: 'Can I access my digital safe from anywhere?',
                answer: 'Yes, Digital Safe includes a mobile app and web interface that allows users to manage the contents of their safe from any location and device with internet access.',
            },
        ],
    },
    {
        icon: Coins,
        title: '$DS Token',
        faqs: [
            {
                question: 'What is the $DS token?',
                answer: "The $DS token is a utility token that supports DigitalSafe platform functionalities. It's not a security and doesn't represent profit sharing or ownership in the company - it functions solely within the platform ecosystem.",
            },
            {
                question: 'How can I use $DS tokens?',
                answer: 'DS tokens can be used for premium services like extended storage, document certification, Proof-of-Authenticity services, transaction fee discounts, and access to exclusive community features.',
            },
            {
                question: 'What is the total supply of $DS tokens?',
                answer: 'The total supply is fixed at 1 billion $DS tokens. No additional tokens can be minted, making it a deflationary token with potential supply reduction through burning mechanisms.',
            },
            {
                question: 'Is $DS an investment or security?',
                answer: "No, $DS is strictly a utility token for platform use. It's not a security, doesn't grant dividend rights, and is not an investment vehicle. Its value derives exclusively from its utility within the platform.",
            },
        ],
    },
    {
        icon: Lock,
        title: 'Security & Compliance',
        faqs: [
            {
                question: 'Is Digital Safe compliant with regulations?',
                answer: 'Yes, Digital Safe is designed to comply with MiCA and other global regulations. The platform uses geographic restrictions, DEX-only model initially, and follows jurisdiction-neutral practices.',
            },
            {
                question: 'What about KYC/AML requirements?',
                answer: 'Initially, KYC/AML is not required since $DS is purchased via smart contract on DEX. Optional KYC may be introduced later for advanced features like DAO participation.',
            },
            {
                question: 'How is user data protected?',
                answer: 'User data is protected through advanced encryption, secure database storage, blockchain technology, and decentralized storage methods. All transactions are tracked and recorded securely.',
            },
            {
                question: "What happens if there's unauthorized access?",
                answer: 'The system includes monitoring and alerting features that notify users of suspicious activity, unauthorized access attempts, or other potential threats in real-time.',
            },
        ],
    },
];
</script>

<template>
    <Head title="Frequently asked questions">
        <meta
            head-key="description"
            name="description"
            content="Find answers to common questions about Digital Safe platform, $DS tokens, and our security features."
        />
        <meta
            name="keywords"
            content="Digital Safe, Frequently Asked Questions, Common Questions, Digital Safe Platform, $DS Token, Security & Compliance, Is Digital Safe compliant with regulations?, What about KYC/AML requirements?, How is user data protected?, What happens if there's unauthorized access?, Is $DS an investment or security?, What is the total supply of $DS tokens?, How can I use $DS tokens?, What is the $DS token?, Can I access my digital safe from anywhere?, What security measures are in place?, How does Digital Safe work?, What is Digital Safe?"
        />
    </Head>
    <div class="min-h-screen bg-background">
        <Header />
        <main class="pt-20">
            <!-- Hero Section -->
            <section class="relative overflow-hidden py-20">
                <div class="bg-gradient-subtle absolute inset-0 opacity-50"></div>
                <div class="relative z-10 container mx-auto max-w-4xl px-4 text-center">
                    <h1 class="mb-6 text-4xl font-bold md:text-6xl">
                        Frequently Asked <span class="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Questions</span>
                    </h1>
                    <p class="text-lg leading-relaxed text-muted-foreground md:text-xl">
                        Find answers to common questions about Digital Safe platform, $DS tokens, and our security features.
                    </p>
                </div>
            </section>

            <!-- Coming Soon Notice -->
            <section class="py-12">
                <div class="container mx-auto px-4">
                    <div class="mx-auto max-w-2xl text-center">
                        <div class="glass-card p-8">
                            <div class="glow mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-lg bg-gradient-hero">
                                <HelpCircle class="h-8 w-8 text-primary-foreground" />
                            </div>
                            <h2 class="mb-4 text-2xl font-semibold">FAQ Section in Development</h2>
                            <p class="mb-6 text-muted-foreground">
                                We're working on comprehensive FAQ content. More information coming soon as we finalize our platform features and
                                gather common questions from our community.
                            </p>
                            <div class="glass-card inline-block px-6 py-3">
                                <span class="font-semibold text-primary">Status: In Development</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- FAQ Categories -->
            <section class="py-20">
                <div class="container mx-auto max-w-4xl px-4">
                    <div class="mb-16 text-center">
                        <h2 class="mb-4 text-3xl font-bold md:text-4xl">Common <span class="text-primary">Questions</span></h2>
                        <p class="text-lg text-muted-foreground">
                            Based on early feedback and platform development, here are some anticipated questions
                        </p>
                    </div>

                    <div class="space-y-12">
                        <div v-for="(category, index) in faqCategories" :key="index" class="glass-card p-8">
                            <div class="mb-8 flex items-center space-x-4">
                                <div class="glow flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-hero">
                                    <component :is="category.icon" class="h-6 w-6 text-primary-foreground" />
                                </div>
                                <h3 class="text-2xl font-semibold">{{ category.title }}</h3>
                            </div>

                            <Accordion type="single" collapsible class="w-full space-y-4">
                                <AccordionItem
                                    v-for="(faq, faqIndex) in category.faqs"
                                    :key="faqIndex"
                                    :value="`${index}-${faqIndex}`"
                                    class="rounded-lg border border-glass-border px-6"
                                >
                                    <AccordionTrigger class="text-left transition-colors hover:text-primary">{{ faq.question }}</AccordionTrigger>
                                    <AccordionContent class="leading-relaxed text-muted-foreground">{{ faq.answer }}</AccordionContent>
                                </AccordionItem>
                            </Accordion>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Quick Help -->
            <section class="bg-gradient-subtle py-20">
                <div class="container mx-auto max-w-4xl px-4">
                    <div class="mb-12 text-center">
                        <h2 class="mb-4 text-3xl font-bold md:text-4xl">Need More <span class="text-primary">Help?</span></h2>
                        <p class="text-lg text-muted-foreground">Can't find what you're looking for? We're here to help!</p>
                    </div>

                    <div class="grid grid-cols-1 gap-8 md:grid-cols-2">
                        <div class="glass-card hover:glow p-8 text-center transition-all duration-300">
                            <div class="glow mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-lg bg-gradient-hero">
                                <Mail class="h-8 w-8 text-primary-foreground" />
                            </div>
                            <h3 class="mb-3 text-xl font-semibold">Email Support</h3>
                            <p class="mb-6 text-muted-foreground">Send us your questions directly and we'll get back to you as soon as possible.</p>
                            <a
                                href="mailto:peter@digitalsafe.io?subject=FAQ - Digital Safe Question"
                                class="hover:glow inline-flex items-center space-x-2 rounded-lg bg-gradient-hero px-6 py-3 font-semibold text-primary-foreground transition-all duration-300"
                            >
                                <Mail class="h-4 w-4" />
                                <span>peter@digitalsafe.io</span>
                            </a>
                        </div>

                        <div class="glass-card hover:glow p-8 text-center transition-all duration-300">
                            <div class="glow mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-lg bg-gradient-hero">
                                <MessageCircle class="h-8 w-8 text-primary-foreground" />
                            </div>
                            <h3 class="mb-3 text-xl font-semibold">Community Feedback</h3>
                            <p class="mb-6 text-muted-foreground">Help us improve our FAQ by suggesting questions that should be included.</p>
                            <a
                                href="mailto:peter@digitalsafe.io?subject=FAQ Suggestion - Digital Safe"
                                class="inline-flex items-center space-x-2 rounded-lg border border-primary px-6 py-3 font-semibold text-primary transition-all duration-300 hover:bg-primary hover:text-primary-foreground"
                            >
                                <MessageCircle class="h-4 w-4" />
                                <span>Suggest a Question</span>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Disclaimer -->
            <section class="py-12">
                <div class="container mx-auto max-w-4xl px-4">
                    <div class="glass-card p-6 text-center">
                        <p class="text-sm text-muted-foreground">
                            <strong class="text-accent">Disclaimer:</strong> This FAQ section is based on current development plans and may be updated
                            as the platform evolves. For the most current information, please contact us directly or check our official announcements.
                        </p>
                    </div>
                </div>
            </section>
        </main>
        <Footer />
    </div>
</template>
